/**
 * Typed GraphQL reads for how a place's last events went (place manager,
 * unit 2). Read-only: nothing in this module writes.
 *
 * Three small reads, not one nested read, because of the backend's query cost
 * limit (graphql-armor, 5000, in `reloaded-rest/src/graphql.ts`): it multiplies
 * a field's cost by its `first:` and compounds with depth, so one read of a
 * place's seasons, their events and each event's leaderboard is refused
 * ("Unexpected error"). So:
 *   1. {@link PlaceSeasonsQuery}: the place's seasons with their dates.
 *   2. {@link SeasonEventsQuery}: one season's events, a page at a time
 *      (`Season.events`, newest first by start time). A season's recurring
 *      events are all created when the season is set up, so the first pages of
 *      a young season can be all still to come; `last-events.ts` pages past them.
 *   3. {@link EventResultsQuery}: for the events shown, in one read (one
 *      alias per event), who came, the tables each opened and the top three.
 * `place-events-api.test.ts` keeps each read under that limit.
 *
 * Events reach a place here only through its seasons (`Place.seasons` →
 * `Season.events`): an event hosted at a place with no season of its own is
 * not reachable from the place (the schema has no `Place.events`).
 *
 * "Tables" is `Event.gatherings` (every table the event opened, closed ones
 * included, never the lobby), not `Event.gatheringCount`: that is the number
 * of tables to set up at the start, and matchmaking events set up none.
 */

import { graphql, gqlRequestAs, type RequestOptions, type ResultOf } from '@/lib/graphql';
import type { RequestViewer } from '@/lib/request-headers';

/** The place's seasons, newest first by start (the 20 newest: `SEASONS_LISTED`
 *  in `last-events.ts`, tied by a test). For everyone but staff, seasons not
 *  yet revealed and finished seasons past their hide date are left out. */
export const PlaceSeasonsQuery = graphql(`
  query PlaceSeasons($id: ID!) {
    place(id: $id) {
      id
      seasons(first: 20) {
        totalCount
        nodes {
          id
          title
          startAt
          endAt
        }
      }
    }
  }
`);

/** A place's seasons, as the first read returns them. */
export type PlaceSeasonsData = NonNullable<ResultOf<typeof PlaceSeasonsQuery>['place']>;

/** A place's seasons. Null when the place does not exist or the caller cannot
 *  see it. `options.signal` aborts the request (as do the two reads below). */
export async function fetchPlaceSeasons(
  placeId: string,
  viewer: RequestViewer,
  options?: RequestOptions,
): Promise<PlaceSeasonsData | null> {
  const data = await gqlRequestAs(viewer, PlaceSeasonsQuery, { id: placeId }, options);
  return data.place;
}

/** One page of a season's events, newest first by start time (100 a page:
 *  `EVENTS_PAGE` in `last-events.ts`, tied by a test), light fields only.
 *  `endAt` is how a cancelled event is told apart: `cancelEvent` (before the
 *  start) sets it to the moment of cancelling, so it ends before it starts.
 *
 *  `Season.events` applies the same visibility rule as `Query.event`, so a
 *  partner's read here lists only events they may see; `last-events.ts` still
 *  drops any whose results read null for a partner. */
export const SeasonEventsQuery = graphql(`
  query SeasonEvents($id: ID!, $offset: Int!) {
    season(id: $id) {
      id
      events(first: 100, offset: $offset) {
        totalCount
        nodes {
          id
          title
          startAt
          endAt
          stateUIHint
        }
      }
    }
  }
`);

/** One page of a season's events: `totalCount` is the season's whole count. */
export type SeasonEventPage = NonNullable<ResultOf<typeof SeasonEventsQuery>['season']>['events'];

/** A page of a season's events from `offset`. Null when the season does not
 *  exist or is archived. */
export async function fetchSeasonEvents(
  seasonId: string,
  offset: number,
  viewer: RequestViewer,
  options?: RequestOptions,
): Promise<SeasonEventPage | null> {
  const data = await gqlRequestAs(viewer, SeasonEventsQuery, { id: seasonId, offset }, options);
  return data.season?.events ?? null;
}

/** One event's results: who came (`totalVisitorCount`, set when the event
 *  ends), the tables it opened, and its top three (`rankings(first: 3)`, tied to
 *  `TOP_SHOWN` in `last-events.ts` by a test). */
export const EventResultFields = graphql(`
  fragment EventResultFields on Event {
    id
    totalVisitorCount
    gatherings {
      id
    }
    leaderboard {
      rankings(first: 3) {
        user {
          id
          public {
            displayName
            username
            photoURL
          }
        }
      }
    }
  }
`);

/** One event's results, as the per-event read returns them. */
export type EventResultData = ResultOf<typeof EventResultFields>;

/** The most events one results read asks for (`EVENTS_SHOWN` in
 *  `last-events.ts`, tied by a test): one alias each, `e0` always, the others
 *  only when `withN` is true. */
export const RESULTS_PER_READ = 5;

export const EventResultsQuery = graphql(
  `
  query EventResults(
    $e0: String!
    $e1: String!
    $e2: String!
    $e3: String!
    $e4: String!
    $with1: Boolean!
    $with2: Boolean!
    $with3: Boolean!
    $with4: Boolean!
  ) {
    e0: event(id: $e0) {
      ...EventResultFields
    }
    e1: event(id: $e1) @include(if: $with1) {
      ...EventResultFields
    }
    e2: event(id: $e2) @include(if: $with2) {
      ...EventResultFields
    }
    e3: event(id: $e3) @include(if: $with3) {
      ...EventResultFields
    }
    e4: event(id: $e4) @include(if: $with4) {
      ...EventResultFields
    }
  }
`,
  [EventResultFields],
);

/** Several events' results in one read, by public id, in the order asked:
 *  null for an event that does not exist or the caller cannot see. At most
 *  {@link RESULTS_PER_READ} ids. */
export async function fetchEventResults(
  eventIds: readonly string[],
  viewer: RequestViewer,
  options?: RequestOptions,
): Promise<(EventResultData | null)[]> {
  if (eventIds.length === 0) return [];
  if (eventIds.length > RESULTS_PER_READ) throw new Error(`At most ${RESULTS_PER_READ} events per results read.`);
  const [e0, e1 = e0, e2 = e0, e3 = e0, e4 = e0] = eventIds as [string, ...string[]];
  const n = eventIds.length;
  const data = await gqlRequestAs(
    viewer,
    EventResultsQuery,
    { e0, e1, e2, e3, e4, with1: n > 1, with2: n > 2, with3: n > 3, with4: n > 4 },
    options,
  );
  return [data.e0, data.e1, data.e2, data.e3, data.e4].slice(0, n).map((r) => r ?? null);
}
